import React from 'react';
// MUI material's Typography and Button used to live here, but pulling them
// in forced the entire @mui/material chunk (~135 KB gzipped) onto the home
// page critical path. We only need theme.typography.h2 and a styled button
// — both achievable with @mui/system's styled() + the existing theme. The
// home entry now loads only the @mui/system chunk (~40 KB), letting LCP
// fire earlier on simulated mobile.
import { styled } from '@mui/system';
import { Link } from 'react-router-dom';
import { Trans } from 'react-i18next';
import { getLocalizedPath } from 'helpers/getLocale';
import useTranslate from 'hooks/useTranslate';
import highlightBrand from 'helpers/highlightBrand';
import config from 'config';

// The hero background used to be bg.svg — a 164 KB illustration of a pastel
// gradient + a halftone dot cluster. It's now reproduced entirely in CSS on
// the Content component below: 0 bytes, paints on the first frame, no fetch,
// no flash. See the `background` + `&::before` rules there.

// Hero's CDN-served troll image — the home page's main above-the-fold asset.
// Preload now lives in index.html (static) instead of being emitted from
// JSX. The previous approach relied on TestStart's lazy chunk loading and
// React 19 hoisting the <link> into <head> — by which point the browser had
// already finished parsing HTML, so the preload fired too late to overlap
// with the initial JS download. Static head preload starts the fetch during
// HTML parse and saves ~1.9s of `lcpLoadDelay` per Lighthouse.

const Background = styled('section')(({ theme }) => ({
  position: 'relative',
  backgroundColor: theme.palette.white.main
}));

const Container = styled('div')(({ theme }) => ({
  maxWidth: 1030,
  width: '100%',
  margin: '0 auto',
  // White spacing above & below the SVG background
  padding: '50px 0',
  boxSizing: 'border-box',
  [theme.breakpoints.down('lg')]: {
    maxWidth: '100%',
    padding: 50
  },
  [theme.breakpoints.down('md')]: {
    padding: 30
  },
  [theme.breakpoints.down('sm')]: {
    padding: 20
  }
}));

const Content = styled('div')(({ theme }) => ({
  position: 'relative',
  padding: '80px 60px',
  boxSizing: 'border-box',
  // Hero background, pure CSS — replaces the 164 KB bg.svg entirely. The
  // pastel diagonal gradient mirrors the SVG's own <linearGradient>: stops
  // #fafdb9 / #a7fa9f / #9cfeda / #b3d9fe at offsets 0 / .327 / .683 / 1,
  // along a ~135° line. It paints on the first frame: no asset fetch, no flash.
  background:
    'linear-gradient(135deg, #fafdb9 0%, #a7fa9f 33%, #9cfeda 68%, #b3d9fe 100%)',
  borderRadius: 24,
  overflow: 'hidden',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 40,
  // The halftone dot cluster the SVG used to bake in: a white dot grid
  // (radial-gradient tiled via background-size) faded into a soft cluster on
  // the right with a radial mask — so dots are dense at the cluster centre
  // and vanish toward the edges, like the original.
  '&::before': {
    content: '""',
    position: 'absolute',
    inset: 0,
    backgroundImage:
      'radial-gradient(rgba(255, 255, 255, 1) 3.1px, transparent 3.4px)',
    backgroundSize: '14.5px 14.5px',
    // #000 holds a solid plateau out to 45% so dots in the cluster core
    // render fully opaque (white) — then fades to transparent by 80%.
    WebkitMaskImage:
      'radial-gradient(ellipse 58% 55% at 61% 51%, #000 0%, #000 45%, transparent 80%)',
    maskImage:
      'radial-gradient(ellipse 58% 55% at 61% 51%, #000 0%, #000 45%, transparent 80%)',
    pointerEvents: 'none'
  },
  // Keep the hero's real content above the decorative dot layer.
  '& > *': {
    position: 'relative',
    zIndex: 1
  },
  [theme.breakpoints.down('lg')]: {
    padding: 50
  },
  [theme.breakpoints.down('md')]: {
    padding: 30,
    flexDirection: 'column',
    textAlign: 'left',
    gap: 30
  },
  [theme.breakpoints.down('sm')]: {
    padding: 20
  }
}));

const Left = styled('div')(({ theme }) => ({
  flex: '0 0 auto',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  [theme.breakpoints.down('md')]: {
    width: '100%'
  }
}));

// Modern browsers (Chrome 88+, all current evergreen) derive an implicit
// aspect-ratio from <img width=… height=…> attributes, so the box is reserved
// at the right ratio before the network image resolves — no explicit
// `aspect-ratio` CSS needed. Adding one was making the image-aspect-ratio
// audit fail because CSS width: 300 overrode the displayed width while the
// rule pinned 285/325.
const Troll = styled('img')(({ theme }) => ({
  width: 300,
  height: 'auto',
  display: 'block',
  [theme.breakpoints.down('md')]: {
    width: 260
  },
  [theme.breakpoints.down('sm')]: {
    width: 200
  }
}));

const Right = styled('div')(({ theme }) => ({
  flex: '1 1 0',
  minWidth: 0,
  maxWidth: 640,
  backgroundColor: 'rgba(255, 255, 255, 0.86)',
  backdropFilter: 'blur(6px)',
  borderRadius: 24,
  padding: 40,
  boxShadow: '0 16px 45px rgba(0, 0, 0, 0.16)',
  [theme.breakpoints.down('md')]: {
    width: '100%',
    padding: 28
  },
  [theme.breakpoints.down('sm')]: {
    padding: 20
  }
}));

// Plain <h1> styled with the project's h1 tokens (see MuiTypography.h1
// styleOverrides in src/theme/index.js). `theme.typography` was never
// customised, so `...theme.typography.h1` spreads MUI's default Roboto —
// inlining keeps the hero stable through font swap and matches the design.
const Title = styled('h1')(({ theme }) => ({
  margin: 0,
  marginBottom: 16,
  fontFamily: 'Unbounded',
  fontSize: 36,
  fontWeight: 600,
  lineHeight: '40px',
  color: theme.palette.textPrimary.main,
  [theme.breakpoints.down('sm')]: {
    fontSize: 28,
    lineHeight: '36px'
  }
}));

// Same fix for body1 — was pulling MUI's default Roboto/16px instead of the
// project's Rubik. Hardcoded so the description renders in Rubik even before
// the @font-face swap completes (font-display: swap means the fallback shows
// briefly; without a matching fontFamily it was Times New Roman).
const Description = styled('p')(({ theme }) => ({
  margin: 0,
  fontFamily: 'Rubik-Regular',
  fontSize: 18,
  fontWeight: 400,
  lineHeight: '28px',
  color: theme.palette.textPrimary.main,
  opacity: 0.9,
  [theme.breakpoints.down('sm')]: {
    fontSize: 16,
    lineHeight: '24px'
  }
}));

const Actions = styled('div')(() => ({
  marginTop: 26,
  display: 'flex',
  justifyContent: 'center',
  width: '100%'
}));

// Was styled(Button variant="contained" color="primary"); the only
// MUI behaviour we relied on was the primary palette + contained look,
// both reproducible directly. Rendered via react-router Link so the
// hover/focus chain stays accessible.
const ButtonStyled = styled(Link)(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: 25,
  padding: '12px 22px',
  // Match Rubik-Regular explicitly so the button doesn't fall back to the
  // browser default (serif on most browsers) before the @font-face swap.
  fontFamily: 'Rubik-Regular',
  fontSize: 16,
  fontWeight: 500,
  lineHeight: 1.75,
  textDecoration: 'none',
  textTransform: 'uppercase',
  letterSpacing: '0.02857em',
  // Salad-lime is the project's default fill for non-transparent buttons
  // (see palette.primary.salad in src/theme/index.js). primary.main is the
  // darker brand green used for highlights/hover, not for button fills.
  backgroundColor: theme.palette.primary.salad,
  color: theme.palette.textPrimary.main,
  cursor: 'pointer',
  transition: 'background-color 0.2s ease',
  '&:hover': {
    backgroundColor: theme.palette.primary.bright
  },
  '&:focus-visible': {
    outline: `2px solid ${theme.palette.primary.main}`,
    outlineOffset: 2
  }
}));

const BrandHighlight = ({ children }) => (
  <strong>{highlightBrand(children)}</strong>
);

const TestStart = () => {
  const tHome = useTranslate('Home');
  const cta = tHome('TestStartCTA');
  return (
    <Background id="teststart">
      <Container>
        <Content>
          <Right>
            <Title>{tHome('TestStartTitle')}</Title>
            <Description>
              <Trans
                i18nKey="Home.TestStartBody"
                components={{ 1: <BrandHighlight /> }}
              />
            </Description>
            <Actions>
              <ButtonStyled to={getLocalizedPath('/assessment')} aria-label={cta}>
                {cta}
              </ButtonStyled>
            </Actions>
          </Right>
          <Left>
            {/* LCP target on the home page. Must be eagerly loaded with
                fetchpriority=high so Chrome discovers + prioritises it; the
                previous loading="lazy" delayed LCP by ~1.9 s (lcpLoadDelay
                in the Lighthouse trace). */}
            <Troll
              src={`${config.imageSource}/904a0ae0-052c-49d2-c0e1-ae652f565800/w285h325`}
              alt="Troll with laptop"
              loading="eager"
              fetchPriority="high"
              width="285"
              height="325"
            />
          </Left>
        </Content>
      </Container>
    </Background>
  );
};

export default TestStart;
